import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import {
  getCategories,
  addCategory,
  addExpenses,
  updatedExpense,
} from "../../services/services";
import { X } from "lucide-react";

const AddExpense = ({ onClose, fetchExpenses, editData }) => {
  const [formData, setFormData] = useState({
    title: "",
    paidTo: "",
    amount: "",
    paymentMethod: "Cash",
    category: "",
    status: "paid",
    note: "",
    date: moment().format("YYYY-MM-DD"),
  });

  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const titleRef = useRef(null);
  const amountRef = useRef(null);

  const paymentOptions = ["Cash", "Bank Transfer", "Card", "UPI"];

  const statusOptions = [
    { value: "paid", label: "Paid" },
    { value: "pending", label: "Pending" },
    // { value: "overdue", label: "Overdue" },
  ];

  // Load categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getCategories({ type: "expense" }); // proper payload
        if (res.data.status) {
          setCategories(res.data.response || []);
        }
      } catch (err) {
        toast.error("Failed to load categories");
      }
    };
    fetchCategories();
  }, []);

  // Set form data when editing
  useEffect(() => {
    if (editData) {
      setFormData({
        title: editData.title || "",
        paidTo: editData.paidTo || "",
        amount: editData.amount?.toString() || "",
        paymentMethod: editData.paymentMethod || "Cash",
        category: editData.category || "",
        status: editData.status || "paid",
        note: editData.note || "",
        date: editData.date
          ? moment(editData.date).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
      });    }
  }, [editData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // ---- AMOUNT FIELD SPECIAL HANDLING ----
    if (name === "amount") { 
      const valid = /^\d*\.?\d*$/.test(value);

      if (!valid) return;

      setFormData((prev) => ({
        ...prev,
        amount: value,
      }));

      if (errors.amount) {
        setErrors((prev) => ({ ...prev, amount: "" }));
      }

      return;
    }

    // ---- NORMAL FIELDS ----
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Expense title is required";
    }

    if (!formData.amount || Number(formData.amount) <= 0) {
      newErrors.amount = "Please enter a valid amount";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      // 🔔 Toast first error
      toast.error(Object.values(newErrors)[0]);

      // 🎯 Focus first invalid field
      if (newErrors.title) {
        titleRef.current?.focus();
      } else if (newErrors.amount) {
        amountRef.current?.focus();
      }

      return;
    }

    setErrors({});
    const payload = {
      ...formData,
      amount: Number(formData.amount),
    };

    try {
      let res;
      if (editData?._id) {
        res = await updatedExpense(editData._id, payload);
      } else {
        res = await addExpenses(payload);
      }

      toast.success(res.data.message || "Expense saved!");
      onClose();
      fetchExpenses?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save expense");
    }
  };

  return (
    <>
      <div
        className="modal fade show d-block"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content shadow-lg">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title fw-bold">
                {editData?._id ? "Edit Expense" : "Add New Expense"}
              </h5>
              <X onClick={onClose} size={24} style={{ cursor: "pointer" }} />
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Title & Paid To */}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">
                      Expense For <span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      ref={titleRef}
                      type="text"
                      name="title"
                      className={`form-control ${
                        errors.title ? "is-invalid" : ""
                      }`}
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="e.g. Office Rent, Electricity Bill"
                    />
                    {errors.title && (
                      <div className="invalid-feedback">{errors.title}</div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">
                      Paid To (Optional)
                    </label>
                    <input
                      type="text"
                      name="paidTo"
                      className="form-control"
                      value={formData.paidTo}
                      onChange={handleChange}
                      placeholder="e.g. Landlord, Vendor name"
                    />
                  </div>
                </div>

                {/* Amount & Payment Method */}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">
                      Amount
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      ref={amountRef}
                      type="text"
                      inputMode="decimal"
                      name="amount"
                      className={`form-control ${
                        errors.amount ? "is-invalid" : ""
                      }`}
                      value={formData.amount}
                      onChange={handleChange}
                      placeholder="0.00"
                    />
                    {errors.amount && (
                      <div className="invalid-feedback">{errors.amount}</div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">
                      Payment Method
                    </label>
                    <select
                      name="paymentMethod"
                      className="form-select"
                      value={formData.paymentMethod}
                      onChange={handleChange}
                    >
                      {paymentOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Category & Status */}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Category</label>

                    <select
                      name="category"
                      className={`form-select `}
                      value={formData.category}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                    >
                      <option value="" disabled>
                        Select category
                      </option>

                      {categories.length === 0 ? (
                        <option value="" disabled>
                          No categories available
                        </option>
                      ) : (
                        categories.map((cat) => (
                          <option key={cat._id} value={cat.name}>
                            {cat.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  {/* STATUS WITH COLOR BADGE */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">
                      Status
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <select
                      name="status"
                      className="form-select"
                      value={formData.status}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                    >
                      {statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Date */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Date</label>
                  <DatePicker
                    selected={formData.date ? new Date(formData.date) : null}
                    onChange={(date) =>
                      setFormData((prev) => ({
                        ...prev,
                        date: date ? moment(date).format("YYYY-MM-DD") : "",
                      }))
                    }
                    className="form-control"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select date"
                  />
                </div>

                {/* Note */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Note (Optional)
                  </label>
                  <textarea
                    name="note"
                    className="form-control"
                    rows="3"
                    value={formData.note}
                    onChange={handleChange}
                    placeholder="Any additional details..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary px-4">
                  {editData?._id ? "Update Expense" : "Save Expense"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddExpense;
